/**
 * Toggle between mock data and real backend queries.
 *
 * Resolution order (first match wins):
 *   1. Explicit `VITE_USE_MOCK_DATA=true|false` env override.
 *   2. If `VITE_INSTANT_APP_ID` is set → live mode (no mock).
 *      InstantDB is the primary backend; pages should never bake in
 *      seed-mock arrays when InstantDB is configured.
 *   3. Fall back to mock mode (no backend configured).
 *
 * The hook flavor is provided so consumers can read the value reactively if
 * we ever decide to flip it from runtime config; today it's compile-time.
 */

function hasInstantAppId(): boolean {
  const raw = import.meta.env.VITE_INSTANT_APP_ID;
  if (!raw) return false;
  const trimmed = String(raw).trim();
  if (!trimmed) return false;
  if (trimmed.includes("your-app-id") || trimmed.includes("xxxxx")) {
    return false;
  }
  return true;
}

export function isMockMode(): boolean {
  // Explicit override wins.
  const flag = import.meta.env.VITE_USE_MOCK_DATA;
  if (flag === "true") return true;
  if (flag === "false") return false;
  // InstantDB configured → never mock. Pages get empty arrays as
  // initialValue, and the live InstantDB query drives the actual rows.
  if (hasInstantAppId()) return false;
  // No backend configured — default to mock mode.
  return true;
}

export function useMockData(): boolean {
  return isMockMode();
}

/**
 * MOCK DATA IS DEAD.
 *
 * The user demanded all demo data be deleted, not just gated. To accomplish
 * this without touching every Mock file, both helpers below ALWAYS return
 * empty. Every `export const mockXxx = emptyIfInstant<T>([...])` collapses
 * to `[]` regardless of env. The arrays inside are unreachable code that the
 * minifier may drop. Mock data is, in effect, deleted from the runtime.
 *
 * TypeScript type definitions in the Mock files (MockDealer, MockInvoice,
 * etc.) are preserved because the rest of the app imports them for typing.
 * Only the data is gone.
 *
 * @deprecated These helpers exist only to make the no-op transition clean.
 *             A future commit may strip the wrappers and the Mock files
 *             entirely once nothing references them.
 */
export function emptyIfInstant<T>(_arr: T[]): T[] {
  return [];
}

/** Same gate for singleton objects — always returns null. */
export function nullIfInstant<T>(_value: T): T | null {
  return null;
}
